import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, IndianRupee, ShoppingBag, TrendingUp, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SectionHeader } from '@/components/charts/SectionHeader';
import { KpiTile } from '@/components/charts/KpiTile';
import { TrendChart } from '@/components/charts/TrendChart';
import { EmptyState } from '@/components/charts/EmptyState';
import { DailyBreakdown } from '@/components/charts/DailyBreakdown';
import { FunnelChart } from '@/components/charts/FunnelChart';
import { DeltaBadge } from '@/components/charts/DeltaBadge';
import { MotionCard } from '@/components/motion/MotionCard';
import { useDashboard } from '@/hooks/useDashboardContext';
import { useMetric } from '@/hooks/useMetric';
import { useOutletCapabilities, useOutlets } from '@/hooks/useOutlets';
import { useAggregatorFunnel } from '@/hooks/useAggregatorFunnel';
import { useOutletProviderBreakdown } from '@/hooks/useOutletProviderBreakdown';
import { OUTLET_TO_FOODDB } from '@/lib/supabase/fooddb';
import { fmtISTRange, formatINRCompact, formatNumberCompact } from '@/lib/format';
import { cn } from '@/lib/utils';
import { CircuitCanvas } from '@/modules/journey/CircuitCanvas';
import { useTargetJourney } from '@/modules/journey/hooks';
import { OutletGrowthStory } from '@/modules/growth/GrowthJourney';

export function OutletDetailPage() {
  const { outletId = '' } = useParams<{ outletId: string }>();
  const { period, setOutletId } = useDashboard();
  const { data: outlets = [] } = useOutlets();
  const outlet = outlets.find((o) => o.id === outletId);
  const caps = useOutletCapabilities(outletId);

  useEffect(() => {
    if (outletId) setOutletId(outletId);
  }, [outletId, setOutletId]);

  const revenue = useMetric({ outletId, source: 'net_sales', period });
  const orders = useMetric({ outletId, source: 'orders', period });
  const covers = useMetric({ outletId, source: 'covers', period });
  const avgTicket = useMetric({ outletId, source: 'avg_ticket', period });

  // Aggregator depth — only when this outlet has a Food DB slug.
  const hasAggregator = Boolean(OUTLET_TO_FOODDB[outletId]);
  const funnel = useAggregatorFunnel({ outletId, period });
  const breakdown = useOutletProviderBreakdown({ period });
  const myBreakdown = breakdown.data?.outlets.find((o) => o.outletId === outletId);

  if (!outlet && outlets.length > 0) {
    return (
      <div className="space-y-6">
        <Link to="/outlets" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to outlets
        </Link>
        <EmptyState title="Outlet not found" description={`No outlet with id "${outletId}".`} />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <Link to="/outlets" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to outlets
        </Link>
        <SectionHeader
          title={outlet?.name ?? outletId}
          description={
            outlet
              ? `${outlet.city} · ${outlet.type === 'qsr' ? 'QSR' : 'Dine-in'} · ${revenue.current.label} · ${fmtISTRange(revenue.current.start, revenue.current.end)} vs ${fmtISTRange(revenue.previous.start, revenue.previous.end)}`
              : ''
          }
        />
      </div>

      <OutletTargetJourney outletId={outletId} />

      <OutletGrowthStory outletId={outletId} />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiTile
          label="Revenue"
          icon={<IndianRupee className="h-3.5 w-3.5" />}
          metric={revenue.data?.metric}
          loading={revenue.isLoading}
          format={formatINRCompact}
          sparkline={revenue.data?.trend.map((p) => p.current)}
          comparisonRange={{ start: revenue.previous.start, end: revenue.previous.end }}
        />
        <KpiTile
          label="Orders"
          icon={<ShoppingBag className="h-3.5 w-3.5" />}
          metric={orders.data?.metric}
          loading={orders.isLoading}
          format={formatNumberCompact}
          sparkline={orders.data?.trend.map((p) => p.current)}
          comparisonRange={{ start: orders.previous.start, end: orders.previous.end }}
          delay={0.05}
        />
        {caps.dineIn && (
          <KpiTile
            label="Covers"
            icon={<Users className="h-3.5 w-3.5" />}
            metric={covers.data?.metric}
            loading={covers.isLoading}
            format={formatNumberCompact}
            sparkline={covers.data?.trend.map((p) => p.current)}
            comparisonRange={{ start: covers.previous.start, end: covers.previous.end }}
            delay={0.1}
          />
        )}
        <KpiTile
          label="Avg ticket"
          icon={<IndianRupee className="h-3.5 w-3.5" />}
          metric={avgTicket.data?.metric}
          loading={avgTicket.isLoading}
          format={formatINRCompact}
          sparkline={avgTicket.data?.trend.map((p) => p.current)}
          comparisonRange={{ start: avgTicket.previous.start, end: avgTicket.previous.end }}
          delay={0.15}
        />
      </div>

      <MotionCard>
        {revenue.data && revenue.data.trend.length > 0 ? (
          <TrendChart
            title="Revenue trend"
            data={revenue.data.trend}
            formatValue={formatINRCompact}
          />
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Revenue trend</CardTitle>
            </CardHeader>
            <CardContent>
              <EmptyState
                title={revenue.isLoading ? 'Loading…' : 'No data for this period'}
                description="Once sync-sales backfills this period, the trend will render."
              />
            </CardContent>
          </Card>
        )}
      </MotionCard>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <MotionCard delay={0.05}>
          <DailyBreakdown
            title="Revenue by day"
            rows={revenue.data?.trend ?? []}
            loading={revenue.isLoading}
            format={formatINRCompact}
          />
        </MotionCard>
        <MotionCard delay={0.1}>
          <DailyBreakdown
            title="Orders by day"
            rows={orders.data?.trend ?? []}
            loading={orders.isLoading}
            format={formatNumberCompact}
          />
        </MotionCard>
      </div>

      {hasAggregator && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <MotionCard delay={0.15}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  Aggregator funnel
                  <span className="text-2xs font-normal text-muted-foreground">
                    Swiggy + Zomato combined
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <FunnelChart
                  stages={funnel.data?.stages ?? []}
                  loading={funnel.isLoading}
                />
              </CardContent>
            </Card>
          </MotionCard>
          <MotionCard delay={0.2}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  Swiggy vs Zomato
                  <span className="text-2xs font-normal text-muted-foreground">
                    head-to-head
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <SoloHeadToHead breakdown={myBreakdown} loading={breakdown.isLoading} />
              </CardContent>
            </Card>
          </MotionCard>
        </div>
      )}
    </div>
  );
}

interface MyBreakdown {
  swiggy: { netSales: number; deliveredOrders: number; aov: number; funnelConvPct: number; poorRatedPct: number };
  zomato: { netSales: number; deliveredOrders: number; aov: number; funnelConvPct: number; poorRatedPct: number };
  swiggySharePct: number;
}

function SoloHeadToHead({
  breakdown,
  loading,
}: {
  breakdown?: MyBreakdown;
  loading: boolean;
}) {
  if (loading) {
    return <div className="text-sm text-muted-foreground">Loading…</div>;
  }
  if (!breakdown) {
    return <EmptyState title="No aggregator data" description="No Swiggy / Zomato rows for this outlet in the selected period." />;
  }
  const total = breakdown.swiggy.netSales + breakdown.zomato.netSales;
  const swiggyShare = total > 0 ? (breakdown.swiggy.netSales / total) * 100 : 50;

  const rows: { label: string; s: number; z: number; fmt: (n: number) => string; invert?: boolean }[] = [
    { label: 'Net sales', s: breakdown.swiggy.netSales, z: breakdown.zomato.netSales, fmt: formatINRCompact },
    { label: 'Delivered', s: breakdown.swiggy.deliveredOrders, z: breakdown.zomato.deliveredOrders, fmt: formatNumberCompact },
    { label: 'AOV', s: breakdown.swiggy.aov, z: breakdown.zomato.aov, fmt: formatINRCompact },
    { label: 'Funnel conv', s: breakdown.swiggy.funnelConvPct, z: breakdown.zomato.funnelConvPct, fmt: (n) => `${n.toFixed(1)}%` },
    { label: 'Poor-rated', s: breakdown.swiggy.poorRatedPct, z: breakdown.zomato.poorRatedPct, fmt: (n) => `${n.toFixed(1)}%`, invert: true },
  ];

  return (
    <div className="space-y-4">
      <div className="flex h-2 w-full overflow-hidden rounded-md">
        <div className="h-full bg-orange-500" style={{ width: `${swiggyShare}%` }} />
        <div className="h-full bg-rose-500" style={{ width: `${100 - swiggyShare}%` }} />
      </div>
      <div className="grid grid-cols-3 gap-2 text-2xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-orange-500" />
          Swiggy {swiggyShare.toFixed(0)}%
        </span>
        <span className="text-center">share</span>
        <span className="flex items-center justify-end gap-1.5">
          Zomato {(100 - swiggyShare).toFixed(0)}%
          <span className="h-2 w-2 rounded-full bg-rose-500" />
        </span>
      </div>
      <ul className="divide-y divide-border/40">
        {rows.map((r) => {
          const swiggyWins = r.invert ? r.s < r.z : r.s > r.z;
          const gap = (() => {
            const max = Math.max(r.s, r.z);
            const min = Math.min(r.s, r.z);
            return min > 0 ? ((max - min) / min) * 100 : 0;
          })();
          const tie = Math.abs(r.s - r.z) / Math.max(r.s, r.z || 1) < 0.02;
          return (
            <li key={r.label} className="grid grid-cols-7 items-center gap-2 py-2 text-sm">
              <span className="col-span-2 text-muted-foreground">{r.label}</span>
              <span
                className={cn(
                  'col-span-2 text-right font-mono tabular-nums',
                  !tie && swiggyWins && 'font-semibold text-orange-600 dark:text-orange-400',
                )}
              >
                {r.fmt(r.s)}
              </span>
              <span
                className={cn(
                  'col-span-2 text-right font-mono tabular-nums',
                  !tie && !swiggyWins && r.z > 0 && 'font-semibold text-rose-600 dark:text-rose-400',
                )}
              >
                {r.fmt(r.z)}
              </span>
              <span className="text-right text-2xs">
                {tie ? (
                  <span className="text-muted-foreground">—</span>
                ) : (
                  <DeltaBadge deltaPct={swiggyWins ? gap : -gap} comparable size="sm" />
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function OutletTargetJourney({ outletId }: { outletId: string }) {
  const { data, isLoading } = useTargetJourney(outletId);
  if (isLoading || !data) return null;
  return (
    <div className="space-y-2">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Target Journey · this outlet
      </div>
      <CircuitCanvas state={data} />
    </div>
  );
}
